import { createClient } from '@supabase/supabase-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error('Missing Supabase environment variables. Please check your .env file.');
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey);

export const signUp = async (data: {
  email: string;
  password: string;
  options?: {
    data?: {
      name: string;
    };
  };
}) => {
  const { data: authData, error } = await supabase.auth.signUp({
    email: data.email,
    password: data.password,
    options: data.options
  });

  if (error) throw error;
  return { data: authData, error: null };
};

export const createUserProfile = async (userId: string, email: string, name: string) => {
  const { data, error } = await supabase
    .from('users')
    .upsert([
      {
        id: userId,
        email,
        name,
        preferences: {
          pushNotifications: true,
          emailNotifications: true
        }
      }
    ])
    .select()
    .single();

  if (error) throw error;
  return data;
};

export const signIn = async (email: string, password: string) => {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password
  });

  if (error) throw error;
  return { data, error: null };
};

export const signOut = async () => {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
  return { error: null };
};

export const getCurrentUser = async () => {
  const { data: { user }, error } = await supabase.auth.getUser();
  if (error) throw error;
  if (!user) throw new Error('No user found');

  const { data: profile, error: profileError } = await supabase
    .from('users')
    .select('*')
    .eq('id', user.id)
    .maybeSingle();

  if (profileError) throw profileError;
  if (!profile) {
    // If profile doesn't exist, create it
    const profile = await createUserProfile(user.id, user.email!, user.user_metadata?.name || 'User');
    return profile;
  }
  
  return profile;
};

export const getProviderDeals = async (category: string, postcode: string, usage?: number) => {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/providers`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ category, postcode, usage }),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to fetch provider deals');
  }

  return response.json();
};

export const compareDeals = async (params: {
  category: string;
  currentProvider: string;
  currentCost: number;
  postcode: string;
  usage?: number;
}) => {
  const response = await fetch(
    `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/comparators`,
    {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    }
  );

  if (!response.ok) {
    throw new Error('Failed to compare deals');
  }

  return response.json();
};